/**
 * Firestore スキーマの型定義。
 * 正は docs/specs/core-schema.md。**変更できるのは本体セッションのみ。**
 * 事業部モジュールはこの型を読んで使う(書き換え禁止)。
 */
import type { Timestamp } from 'firebase/firestore';

/** 'YYYY-MM-DD'(JST) */
export type DateStr = string;
/** 'YYYY-MM'(JST) */
export type MonthStr = string;
/** 整数(円) */
export type Yen = number;

// ---------------------------------------------------------------------------
// 会社(グループ会社対応・要件定義書 1.5)
// ---------------------------------------------------------------------------

/** 既定の会社。入力時の初期値であり、既存データの補填にも使う */
export const DEFAULT_COMPANY_ID = 'sum';

export type Company = {
  id: string;
  code: string;
  name: string;
  /** SUMのみ true。入力時の初期値に使う */
  is_default: boolean;
  display_order: number;
  active: boolean;
};

// ---------------------------------------------------------------------------
// 事業部
// ---------------------------------------------------------------------------

/**
 * 初期の9事業部。**事業部は増減しうる**(要件定義書 2)ため、
 * この配列は seed とテストの既定値に使うだけで、実行時の判定には使わない。
 * 実行時は departments コレクションを正とする。
 */
export const DEPARTMENT_IDS = [
  'cc',
  'unso',
  'oroshi',
  'famille',
  'kaitori',
  'fudosan-k',
  'fudosan-i',
  'honbu',
  'sccars',
] as const;

/**
 * 事業部ID。**固定の列挙にしない**(事業部は管理者画面から追加できるため)。
 * 初期9事業部は DEPARTMENT_IDS を参照。
 */
export type DepartmentId = string;

export type Department = {
  id: DepartmentId;
  /** 管理番号 D-xx */
  code?: string;
  name: string;
  short_name: string;
  display_order: number;
  module_key: string;
  is_headquarters: boolean;
  active: boolean;
  /**
   * この事業部の部門長(部門長として数える利用者の uid。2026-10-22 HANDOFF #7)。
   * 利用者の保存時に masterRepo が更新する。権限ルールは、空(または未設定)のときだけ
   * 一般の申請者が部門長確認を省略して申請することを許す
   */
  manager_user_ids?: string[];
  created_at: Timestamp;
  updated_at: Timestamp;
};

/**
 * 部門長として数える人か(有効・役割が部門長または承認者・その事業部を担当)。
 * 通知の宛先・部門長確認の要否・事業部の部門長一覧(manager_user_ids)で共通に使う
 */
export function actsAsManagerOf(
  u: Pick<User, 'active' | 'flow_role' | 'role' | 'is_accounting' | 'manager_department_ids'>,
  departmentId: DepartmentId,
): boolean {
  if (!u.active) return false;
  const role = u.flow_role ?? flowRoleFromLegacy(u.role, u.is_accounting);
  return (role === 'bucho' || role === 'approver') && (u.manager_department_ids ?? []).includes(departmentId);
}

/** 事業部ごとの部門長の uid 一覧(並びは uid 順・重複なし) */
export function departmentManagerIds(
  users: Array<Pick<User, 'id' | 'active' | 'flow_role' | 'role' | 'is_accounting' | 'manager_department_ids'>>,
  departmentIds: DepartmentId[],
): Record<DepartmentId, string[]> {
  const out: Record<DepartmentId, string[]> = {};
  for (const d of departmentIds) {
    out[d] = [...new Set(users.filter((u) => actsAsManagerOf(u, d)).map((u) => u.id))].sort();
  }
  return out;
}

// ---------------------------------------------------------------------------
// ユーザー・権限
// ---------------------------------------------------------------------------

export type UserRole = 'admin' | 'manager' | 'member';

/**
 * 承認フロー上の役割(要件定義書v3.5 §3)。メニューの出し分けとフローの担当を決める。
 * member=一般 / bucho=部門長(部門長確認) / approver=承認者(亀山・後藤) / accounting=経理
 */
export const FLOW_ROLES = ['member', 'bucho', 'approver', 'accounting'] as const;
export type FlowRole = (typeof FLOW_ROLES)[number];

export const FLOW_ROLE_LABELS: Record<FlowRole, string> = {
  member: '一般',
  bucho: '部門長',
  approver: '承認者',
  accounting: '経理',
};

/** 役割バッジの色(2026-09-07 指示: 承認者・経理・部門長・一般で色を分ける)。base.css の .role-* */
export const FLOW_ROLE_BADGE_CLASS: Record<FlowRole, string> = {
  approver: 'role-approver',
  accounting: 'role-accounting',
  bucho: 'role-bucho',
  member: 'role-member',
};

/** flow_role 未設定の既存ユーザーの既定値(移行期の互換) */
export function flowRoleFromLegacy(role: UserRole, isAccounting: boolean): FlowRole {
  if (role === 'admin' || isAccounting) return 'accounting';
  if (role === 'manager') return 'bucho';
  return 'member';
}

/**
 * 権限マトリクス(要件定義書 3)。
 * ロール固定ではなく、ユーザーごとに6つの権限をON/OFFする。
 * 役員・責任者・メンバーの線引きが変わっても、画面操作だけで追従できるようにするため。
 */
export const PERMISSION_KEYS = [
  'transaction_input',
  'department_pl',
  'company_pl',
  'approve',
  'confirm',
  'master_admin',
] as const;
export type PermissionKey = (typeof PERMISSION_KEYS)[number];

export type Permissions = Record<PermissionKey, boolean>;

export const PERMISSION_LABELS: Record<PermissionKey, string> = {
  transaction_input: '取引入力',
  department_pl: '自事業部PL閲覧',
  company_pl: '全社PL閲覧',
  approve: '承認',
  confirm: '確定',
  master_admin: 'マスタ管理',
};

export const PERMISSION_DESCRIPTIONS: Record<PermissionKey, string> = {
  transaction_input: '経費申請・売上原価の入力ができる',
  department_pl: '所属事業部のPLを見られる',
  company_pl: '全社PLと予算入力を見られる',
  approve: '所属事業部または全社の経費申請を承認・差戻しできる',
  confirm: '領収書を確認して確定できる。資金繰り(CF)も見られる',
  master_admin: 'マスタ・ユーザー・権限を編集できる',
};

/** ロールから権限を導く(既存ユーザーの移行と、権限未設定時の既定値に使う) */
/** すべての権限(承認者に与える。2026-09-02 発注者指示) */
export const ALL_PERMISSIONS: Permissions = {
  transaction_input: true,
  department_pl: true,
  company_pl: true,
  approve: true,
  confirm: true,
  master_admin: true,
};

/**
 * 役割(flow_role)から権限を決める(2026-09-02)。
 * **承認者はすべての権限を持つ**(マスタ管理を含む)。
 * それ以外は保存済みの権限、なければ従来どおり role から導く
 */
export function permissionsForUser(
  flowRole: FlowRole,
  saved: Permissions | undefined,
  role: UserRole,
  isAccounting: boolean,
): Permissions {
  if (flowRole === 'approver') return { ...ALL_PERMISSIONS };
  return saved ?? permissionsFromRole(role, isAccounting);
}

export function permissionsFromRole(role: UserRole, isAccounting: boolean): Permissions {
  if (role === 'admin') {
    return {
      transaction_input: true,
      department_pl: true,
      company_pl: true,
      approve: true,
      confirm: true,
      master_admin: true,
    };
  }
  return {
    transaction_input: true,
    department_pl: role === 'manager',
    company_pl: false,
    approve: role === 'manager' || isAccounting,
    confirm: isAccounting,
    master_admin: false,
  };
}

export type User = {
  /** docID = Firebase Auth の uid */
  id: string;
  /** ログイン用のメール(内部用)。`<社内ID>@<固定ドメイン>`。表示は社内IDで行う */
  email: string;
  /** 本人の連絡先メールアドレス(案内の送信先。2026-09-03 追加。ログインには使わない) */
  contact_email?: string | null;
  display_name: string;
  role: UserRole;
  /** 経理フラグ。権限マトリクス未設定ユーザーの既定値の算出に使う(表示用にも残す) */
  is_accounting: boolean;
  /** 権限マトリクス。未設定なら permissionsFromRole() の結果を使う */
  permissions?: Permissions;
  /** 承認フロー上の役割。未設定なら flowRoleFromLegacy() で導く */
  flow_role?: FlowRole;
  /** 主所属。未所属(所属事業部なし)のときは null(2026-09-07: 名簿からの一括登録で所属未定のまま入れるため) */
  primary_department_id: DepartmentId | null;
  /** 所属事業部(閲覧・入力範囲)。空なら未所属 */
  department_ids: DepartmentId[];
  /** 責任者を務める事業部(一次承認の権限) */
  manager_department_ids: DepartmentId[];
  /** 本人がパスワードを設定した日時(2026-09-08 指示)。未設定(null)なら、ログイン時に自分のパスワードを決めてもらう */
  password_set_at?: Timestamp | null;
  active: boolean;
  created_at: Timestamp;
  updated_at: Timestamp;
};

// ---------------------------------------------------------------------------
// 勘定科目
// ---------------------------------------------------------------------------

export type AccountCategory = 'revenue' | 'cogs' | 'sgna' | 'asset' | 'liability';
/** 共通サマリー5行のどの行に集計されるか。null は PL 非対象 */
export type PlLine = 'revenue' | 'cogs' | 'expense' | null;

export type Account = {
  id: string;
  code: string;
  name: string;
  category: AccountCategory;
  pl_line: PlLine;
  /** 経費申請の科目①候補に出すか */
  expense_selectable: boolean;
  /** 項目(売上/原価/販管/外収/外費)。管理番号の採番に使用(2026-08-26・勘定科目一覧Excel) */
  item_category?: string | null;
  /** 科目略称(消耗品など)。管理番号の採番に使用 */
  abbr?: string | null;
  /** 科目の説明(申請者向けの補足) */
  description?: string | null;
  /** 経費申請でこの科目を表示する事業部。null/空=全事業部(2026-08-26 事業部制限) */
  department_ids?: DepartmentId[] | null;
  display_order: number;
  active: boolean;
};

export type SubAccount = {
  id: string;
  account_id: string;
  code: string;
  name: string;
  display_order: number;
  active: boolean;
};

// ---------------------------------------------------------------------------
// 承認ルール
// ---------------------------------------------------------------------------

export type ApproverRole = 'department_manager' | 'accounting' | 'admin';

export type ApprovalStep = {
  step: number;
  label: string;
  approver_roles: ApproverRole[];
  required_count: number;
};

export type ApprovalRuleConditions = {
  /** null は制限なし */
  department_ids: DepartmentId[] | null;
  account_ids: string[] | null;
  amount_min: Yen;
  amount_max: Yen | null;
};

export type ApprovalRule = {
  id: string;
  name: string;
  /** 小さいほど優先。最初にマッチした1件だけを適用する */
  priority: number;
  active: boolean;
  conditions: ApprovalRuleConditions;
  steps: ApprovalStep[];
};

// ---------------------------------------------------------------------------
// 階層マスタ(フェーズ2/3で使用)
// ---------------------------------------------------------------------------

export type HierarchyNode = {
  id: string;
  /** 管理番号 H-xxxx */
  code?: string;
  department_id: DepartmentId;
  parent_id: string | null;
  /** 祖先IDの配列(部分木の集計に使う) */
  path: string[];
  name: string;
  level_label: string;
  display_order: number;
  active: boolean;
};

// ---------------------------------------------------------------------------
// 取引
// ---------------------------------------------------------------------------

/**
 * 取引の種別。
 * `transfer`(資金移動=口座間振替)は**PLに一切含めない**。CF上は両口座に出金/入金として現れる。
 */
export type TransactionType = 'income' | 'expense' | 'cash_in' | 'cash_out' | 'transfer';

/** 支払方法(要件定義書 6)。v3.7で 'cash'(現金)を追加 */
export const PAYMENT_METHODS = [
  'cash',
  'bank_transfer',
  'card',
  'direct_debit',
  'petty_cash',
  'reimbursement',
  // 仮払い。経理が先に本人へ仮払いし、本人が購入して精算する(v1.1)
  'prepaid',
  'other',
] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: '現金',
  bank_transfer: '振込',
  card: 'クレカ',
  direct_debit: '口座引落',
  petty_cash: '小口現金',
  reimbursement: '立替',
  prepaid: '仮払い',
  other: 'その他',
};

/** 申請時に選ぶ支払い方法の区分(v3.7)。実際の口座は支払い時に選ぶ */
export const APPLY_PAYMENT_METHODS = ['cash', 'bank_transfer', 'card', 'reimbursement', 'prepaid'] as const satisfies readonly PaymentMethod[];

/** 証憑区分(要件定義書 6) */
export const EVIDENCE_TYPES = ['invoice', 'statement', 'receipt_only'] as const;
export type EvidenceType = (typeof EVIDENCE_TYPES)[number];

export const EVIDENCE_TYPE_LABELS: Record<EvidenceType, string> = {
  invoice: '請求書',
  statement: '支払明細',
  receipt_only: '領収書のみ',
};

/** 取引の入力経路。`recurring` は固定費マスタからの自動計上(=固定費の判定に使う) */
export type TransactionSource =
  | 'manual'
  | 'csv'
  | 'routine'
  | 'recurring'
  /** 経費申請(承認不要)=事後報告(2026-08-26 会議) */
  | 'report'
  /** 振込手数料の自動計上(2026-08-26 会議) */
  | 'transfer_fee';

/**
 * 5段階フロー(要件定義書v3.5 §5)。
 * applied(申請中)→ manager_checked(承認待ち)→ approved(支払い待ち)
 * → paid(支払い済み・実績計上)→ completed(完了・ロック)。差戻しは各段階から可。
 */
export const TRANSACTION_STATUSES = [
  'applied',
  'manager_checked',
  'approved',
  // 現金フロー(v3.8 1-6): 承認後、経理が現金を引き渡し、支払い者が受領してから支払う
  'cash_ready',
  'received',
  'paid',
  'completed',
  'rejected',
  // 申請者本人による取消(2026-08-26)。お金が動く前(支払い・仮払い前)のみ。履歴として残す
  'cancelled',
] as const;
export type TransactionStatus = (typeof TRANSACTION_STATUSES)[number];

/** 画面表示用のラベル(モックアップv2.3準拠) */
export const STATUS_LABELS: Record<TransactionStatus, string> = {
  applied: '申請中',
  manager_checked: '承認待ち',
  approved: '支払い待ち',
  cash_ready: '受領待ち',
  received: '受領済み',
  paid: '支払い済み',
  completed: '完了',
  rejected: '差戻し',
  cancelled: '取消',
};

/** 申請の種別。null は承認フローを通らない取引(売上・原価・固定費・振替) */
export type DocType = 'expense' | 'invoice' | null;

/**
 * 支払い者(申請時に選択)。
 * self=自分で支払う / bucho=部門長(同じ事業部の部門長)に依頼 / accounting=経理に依頼。
 * 部門長本人の申請では bucho(自分自身への依頼)は選べない
 */
export type Payer = 'self' | 'bucho' | 'accounting' | null;

export const PAYER_LABELS: Record<Exclude<Payer, null>, string> = {
  self: '自分',
  bucho: '部門長',
  accounting: '経理',
};

/**
 * 申請フォームの支払い者3択の表示名(v3.7)。
 * 選択肢は固定3択(フローと直結するため追加不可)だが、表示名だけ
 * settings/payer_labels で変更できる(マスタ管理→設定タブ)
 */
export type PayerLabels = Record<Exclude<Payer, null>, string>;
export const DEFAULT_PAYER_SELECT_LABELS: PayerLabels = {
  self: '自分',
  bucho: '部門長',
  accounting: '経理',
};

/**
 * 状態の表示ラベル。申請以外(doc_type=null)の paid は「計上済み」と表示する
 * (入金に「支払い済み」は紛らわしいため。内部値は paid のまま)
 */
export function statusLabelFor(t: Pick<Transaction, 'status' | 'doc_type'>): string {
  if (t.status === 'paid' && !t.doc_type) return '計上済み';
  return STATUS_LABELS[t.status];
}

/**
 * 状態の詳細ラベル(2026-08-26)。申請一覧で「誰の・どの作業待ちか」まで分かるようにする。
 * 例: 仮払い待ち(経理) / 受領待ち(本人) / 支払い待ち(部門長) / 精算待ち(経理) / 受領書待ち(本人)
 */
export function detailedStatusLabel(
  t: Pick<
    Transaction,
    'status' | 'doc_type' | 'payment_method' | 'payer' | 'advance' | 'reimbursed_at' | 'settlement_received_at'
  > &
    Partial<Pick<Transaction, 'source' | 'report_accounting_confirmed_at'>>,
): string {
  // 報告型(経費申請(承認不要)・2026-08-27): 経理確認→承認者確認の確認状況を表示
  if (t.source === 'report') {
    if (t.status === 'completed') return '完了';
    return t.report_accounting_confirmed_at ? '承認者確認待ち' : '経理確認待ち';
  }
  if (!t.doc_type) return statusLabelFor(t);
  switch (t.status) {
    case 'applied':
      return '部門長確認待ち';
    case 'manager_checked':
      return '承認待ち';
    case 'approved':
      if (t.payment_method === 'prepaid') return '仮払い待ち(経理)';
      return `支払い待ち(${t.payer ? PAYER_LABELS[t.payer] : '—'})`;
    case 'cash_ready':
      return '受領待ち(本人)';
    case 'received':
      return '支払い待ち(仮払い金)';
    case 'paid':
      if (t.advance === true) {
        if (!t.reimbursed_at) return '精算待ち(経理)';
        if (!t.settlement_received_at) return '受領書待ち(本人)';
      }
      return '完了確認待ち';
    case 'completed':
      return '完了';
    case 'rejected':
      return '差戻し(再申請待ち)';
    case 'cancelled':
      return '取消';
  }
}

/** 進捗ドット(申請→確認→承認→支払→完了)の点灯数。差戻しは-1 */
export function progressStep(status: TransactionStatus): number {
  switch (status) {
    case 'applied':
      return 1;
    case 'manager_checked':
      return 2;
    case 'approved':
    case 'cash_ready':
    case 'received':
      return 3;
    case 'paid':
      return 4;
    case 'completed':
      return 5;
    case 'rejected':
    case 'cancelled':
      return -1;
  }
}

export type StoredFile = {
  /** Storage上のパス */
  path: string;
  name: string;
  content_type: string;
  size: number;
  uploaded_by: string;
  uploaded_at: Timestamp;
};

export type ApprovalRecord = {
  step: number;
  action: 'approve' | 'reject';
  by: string;
  at: Timestamp;
  comment: string | null;
};

/** 差戻し履歴の1件(v3.8)。誰が・いつ・どの段階から・どのコメントで差戻したか */
export type RejectRecord = {
  at: Timestamp;
  by: string;
  reason: string;
  from_status: TransactionStatus;
};

export type Transaction = {
  id: string;
  /** 管理番号(K-YYYY-#### / I-YYYY-#### / F-YYYY-####)。申請以外の手入力は null */
  code: string | null;
  type: TransactionType;
  status: TransactionStatus;
  /** 申請の種別。null は承認フローを通らない取引(売上・原価・固定費・振替) */
  doc_type: DocType;
  /** 支払い者(申請時に選択)。self=自分で支払う / accounting=経理に依頼 */
  payer: Payer;
  /** 支払い先マスタ(vendors)への参照。申請では必須、それ以外は null 可 */
  vendor_id: string | null;
  /** 請求書申請のみ: 請求書番号(任意)と請求日 */
  invoice_no: string | null;
  invoice_date: DateStr | null;
  /** 所属会社(既定はSUM)。未設定の既存データは 'sum' として扱う */
  company_id: string;
  department_id: DepartmentId;
  account_id: string;
  sub_account_id: string;
  hierarchy_node_id: string | null;

  /** 現在の金額(確定前に経理が修正した場合はその値) */
  amount: Yen;
  /** 申請時の金額 */
  applied_amount: Yen;
  tax_mode: 'inclusive';
  tax_amount: Yen | null;

  /** 計上日 → PLに反映 */
  posting_date: DateStr;
  posting_month: MonthStr;
  /** 支払日 → CFに反映 */
  payment_date: DateStr;
  payment_month: MonthStr;
  fiscal_year: number;
  due_date: DateStr | null;

  counterparty: string | null;
  description: string | null;
  /** 備考(任意・v3.7) */
  note?: string | null;
  /** 支払方法(申請時=区分: 現金/振込/クレカ。支払い時に実際の値へ更新可) */
  payment_method: PaymentMethod | null;
  /** 実際に支払った口座・現金・カード(口座マスタ参照。支払い時に選択・v3.7) */
  payment_account_id?: string | null;
  /** クレカ支払いの購入日(2026-09-04)。PL計上日=購入日、支払日(CF)=引き落とし日 */
  purchase_date?: DateStr | null;
  /** まとめて精算の記録ID(settlements)。2026-09-04 */
  settlement_id?: string | null;
  /**
   * 部門長立替(v3.7)。部門長が自費で立替払いし、後で経理が部門長へ精算する。
   * PLは部門長の支払い時(paid)に計上、CFは経理の精算完了日に計上する。
   * 精算が完了するまで完了確認(completed)はできない
   */
  advance?: boolean;
  reimbursed_at?: Timestamp | null;
  reimbursed_by?: string | null;
  /** 精算日 → CFに反映 */
  reimbursement_date?: DateStr | null;
  reimbursement_cash_entry_id?: string | null;
  /** 差戻し履歴(v3.8)。直近の理由は reject_reason にも入る */
  reject_history?: RejectRecord[];
  /** 現金フロー(v3.8): 経理が現金を引き渡した記録 */
  cash_handed_at?: Timestamp | null;
  cash_handed_by?: string | null;
  /** 仮払い・現金フロー: 支払い者が現金を受領した記録(受領日) */
  received_at?: Timestamp | null;
  received_by?: string | null;
  /** 受領時に添付する記入済み受領書(v1.1 仮払い) */
  received_receipts?: StoredFile[];
  /** 仮払い時にCFへ計上した出金(仮払い) */
  prepaid_cash_entry_id?: string | null;
  /** 立替精算後、本人が記入済み受領書を添付した記録(v1.1 立替型の受領ステップ) */
  settlement_receipts?: StoredFile[];
  settlement_received_at?: Timestamp | null;
  settlement_received_by?: string | null;
  /** フローパターン(flow_patterns)のID。§2の14パターン */
  pattern_id?: string | null;
  /** 証憑区分(請求書・支払明細・領収書のみ) */
  evidence_type: EvidenceType | null;

  /** 申請時の添付(スクショ・見積など) */
  attachments: StoredFile[];
  /** 購入後の領収書 */
  receipts: StoredFile[];

  /** 将来の連結調整用 */
  is_internal: boolean;
  counterparty_department_id: DepartmentId | null;

  approval_rule_id: string;
  /** 適用時点のルールのコピー(後からルールを変えても履歴が壊れない) */
  approval_snapshot: ApprovalStep[];
  current_step: number;
  approvals: ApprovalRecord[];

  applied_at: Timestamp | null;
  applied_by: string | null;
  /** 部門長確認(部門長) */
  manager_checked_at: Timestamp | null;
  manager_checked_by: string | null;
  /** 承認(承認者=亀山・後藤) */
  approved_at: Timestamp | null;
  approved_by: string | null;
  /** 支払い実行(→実績計上) */
  paid_at: Timestamp | null;
  paid_by: string | null;
  /** 完了確認(承認者・以降ロック) */
  completed_at: Timestamp | null;
  completed_by: string | null;
  /** 申請者本人による取消(2026-08-26)。履歴として残す */
  cancelled_at?: Timestamp | null;
  cancelled_by?: string | null;
  /** 旧4段階フローの記録(移行データの表示用に残す) */
  submitted_at: Timestamp | null;
  submitted_by: string | null;
  confirmed_at: Timestamp | null;
  confirmed_by: string | null;
  rejected_at: Timestamp | null;
  rejected_by: string | null;
  reject_reason: string | null;
  rejected_from_status: TransactionStatus | null;

  source: TransactionSource;
  /** 確定時に生成した cash_entries のID */
  cash_entry_id: string | null;
  /** 繰り返し支払い(旧: 固定費マスタ)から作られた場合の元ID */
  recurring_cost_id: string | null;
  /** 繰り返し支払い(2026-09-07): 毎回金額が違う設定なら true(支払い時に確定金額を入力) */
  recurring_amount_varies?: boolean;
  /**
   * 固定費か変動費か(2026-09-24 指示)。承認者が取引ごとに切り替える。
   * 未設定(null)は従来どおり自動判定: 繰り返し支払い=固定 / それ以外=変動
   */
  cost_type?: CostType | null;
  /** 振込手数料(2026-08-26 会議)。経理の振込支払い時に入力。手数料は別取引(支払手数料)として自動計上 */
  transfer_fee?: Yen | null;
  /** source='transfer_fee' の取引が指す、元の申請ID */
  fee_of?: string | null;
  /** 報告型(経費申請(承認不要))の経理確認(2026-08-26 会議)。承認者確認は completed_at/by */
  report_accounting_confirmed_at?: Timestamp | null;
  report_accounting_confirmed_by?: string | null;
  /** 承認者備考(2026-08-27)。完了前の修正フォームで承認者が記入する */
  approver_note?: string | null;

  created_by: string;
  created_at: Timestamp;
  updated_by: string;
  updated_at: Timestamp;
};

/**
 * 固定費・変動費(2026-09-24 指示)。経費管理表の「固/変」の表示・絞り込み・集計に使う。
 * 承認者が詳細の「修正(全項目)」で切り替える(申請者・経理は変えられない)
 */
export const COST_TYPES = ['fixed', 'variable'] as const;
export type CostType = (typeof COST_TYPES)[number];
export const COST_TYPE_LABELS: Record<CostType, string> = { fixed: '固定', variable: '変動' };

/** 固定費か。切り替えていない取引は従来どおり「繰り返し支払い=固定 / それ以外=変動」 */
export function isFixedCost(t: Pick<Transaction, 'source'> & { cost_type?: CostType | null }): boolean {
  if (t.cost_type === 'fixed') return true;
  if (t.cost_type === 'variable') return false;
  return t.source === 'recurring';
}

// --- PL に入る条件(2026-10-22 発注者決定: 経費は承認時点で計上) ------------------------------
// 判定は src/core/pl/plRecognition.ts の plEntryOf() に集約する。ここは状態の分け方だけを持つ。
//   見込み = 申請中・部長確認済み + 計上日の月がまだ来ていない「承認済み以降」
//   実績   = 計上日の月が来た「承認済み以降」
//   対象外 = 差戻し・取消、資金移動(type='transfer')

/** 承認前の状態。PL では常に「見込み」 */
export const PL_BEFORE_APPROVAL_STATUSES: TransactionStatus[] = ['applied', 'manager_checked'];

/** 承認済み以降の状態。計上日の月が来ていれば「実績」、まだなら「見込み」 */
export const PL_APPROVED_STATUSES: TransactionStatus[] = ['approved', 'cash_ready', 'received', 'paid', 'completed'];

/** PL の集計で読み込む状態(Firestore クエリの 'in' 条件用。差戻し・取消は読まない) */
export const PL_QUERY_STATUSES: TransactionStatus[] = [...PL_BEFORE_APPROVAL_STATUSES, ...PL_APPROVED_STATUSES];

/**
 * 実績計上の判定(旧定義: 支払い済み以降のみ)。
 * @deprecated 2026-10-22 から経費は承認時点で計上する。plEntryOf()(src/core/pl/plRecognition.ts)を使う。
 *             Board セッションが PL・経費管理表を新しい判定へ移したら削除する
 */
export function isActualBooked(t: Pick<Transaction, 'status'>): boolean {
  return t.status === 'paid' || t.status === 'completed';
}

/**
 * 実績として集計するステータス(旧定義・Firestoreクエリの 'in' 条件用)。
 * @deprecated 2026-10-22 から PL_QUERY_STATUSES で読み込み、plEntryOf() で実績・見込みを分ける
 */
export const BOOKED_STATUSES: TransactionStatus[] = ['paid', 'completed'];

/**
 * 見込みとして扱うステータス(旧定義・decisions.md 2026-08-21・確認事項1)。
 * @deprecated 2026-10-22 から PL_QUERY_STATUSES で読み込み、plEntryOf() で実績・見込みを分ける
 */
export const PENDING_STATUSES: TransactionStatus[] = ['approved', 'cash_ready', 'received'];

/** 許可されたステータス遷移。ここにない遷移は実行しない */
export const ALLOWED_TRANSITIONS: Record<TransactionStatus, TransactionStatus[]> = {
  applied: ['manager_checked', 'approved', 'rejected', 'cancelled'],
  manager_checked: ['approved', 'rejected', 'cancelled'],
  // 現金(経理以外が支払い)は 経理の現金引渡し(cash_ready)→受領(received)を経由する
  approved: ['cash_ready', 'paid', 'rejected', 'cancelled'],
  cash_ready: ['received', 'rejected'],
  received: ['paid', 'rejected'],
  // 支払い後の差戻しは禁止(2026-10-22 発注者決定。訂正は承認者の修正で行う)。
  // 権限ルール・画面側の禁止は Gate ステップ1で反映する(docs/HANDOFF.md #12)
  paid: ['completed'],
  completed: [],
  rejected: ['applied', 'cancelled'],
  cancelled: [],
};

export function canTransition(from: TransactionStatus, to: TransactionStatus): boolean {
  return ALLOWED_TRANSITIONS[from].includes(to);
}

// ---------------------------------------------------------------------------
// 資金繰り(CF)
// ---------------------------------------------------------------------------

/** 口座=支払い方法マスタの種別(v3.7で 'card' を追加) */
export const CASH_ACCOUNT_KINDS = ['cash', 'bank', 'card'] as const;
export type CashAccountKind = (typeof CASH_ACCOUNT_KINDS)[number];
export const CASH_ACCOUNT_KIND_LABELS: Record<CashAccountKind, string> = {
  cash: '現金',
  bank: '銀行',
  card: 'クレカ',
};

export type CashAccount = {
  id: string;
  /** 所属会社(既定はSUM) */
  company_id: string;
  name: string;
  kind: CashAccountKind;
  bank_name: string | null;
  branch_name: string | null;
  /** 下4桁のみ保持 */
  account_number_masked: string | null;
  opening_balance: Yen;
  /** 集計キャッシュ(settled の増減で更新) */
  current_balance: Yen;
  display_order: number;
  active: boolean;
  /** クレカのみ(2026-09-04): 締め日(1〜31。31=月末) */
  closing_day?: number | null;
  /** クレカのみ: 引き落とし日(1〜31。31=月末) */
  debit_day?: number | null;
  /** クレカのみ: 引き落とし月(1=翌月 / 2=翌々月) */
  debit_month_offset?: 1 | 2 | null;
  /** クレカのみ(2026-09-08): 引き落とし日が土日祝のとき(前の平日/後の平日/そのまま)。省略時はそのまま */
  debit_holiday_shift?: HolidayShift | null;
};

/**
 * まとめて精算(2026-09-04 発注者指示)。複数の立替をまとめて立替者へ支払った記録。
 * 一覧PDF(自動生成)と、受領書(その場でサイン)または振込明細を持つ
 */
export type Settlement = {
  id: string;
  /** 立替者(受け取る人) */
  payee_id: string;
  payee_name: string;
  transaction_ids: string[];
  count: number;
  total_amount: Yen;
  settlement_date: DateStr;
  cash_account_id: string;
  /** 現金=その場で受領書にサイン / 振込=振込明細を添付 */
  method: 'cash' | 'bank_transfer';
  /** 精算一覧PDF(自動生成。現金のときはサイン入り) */
  list_pdf: StoredFile | null;
  /** 振込明細など */
  evidence: StoredFile[];
  created_by: string;
  created_at: Timestamp;
};

export type CashEntry = {
  id: string;
  /** 'transfer' なら資金移動(口座間振替)の片側 */
  kind?: 'transfer';
  cash_account_id: string;
  date: DateStr;
  direction: 'in' | 'out';
  amount: Yen;
  /** planned=予定 / settled=確定 */
  status: 'planned' | 'settled';
  description: string | null;
  counterparty: string | null;
  /** 紐づく取引(経費確定から生成された場合) */
  transaction_id: string | null;
  import_batch_id: string | null;
  /** 重複チェック用ハッシュ(口座+日付+金額+摘要) */
  dedup_hash: string | null;
  matched_at: Timestamp | null;
  matched_by: string | null;
  created_by: string;
  created_at: Timestamp;
  updated_at: Timestamp;
};

export type CashImport = {
  id: string;
  cash_account_id: string;
  file_name: string;
  format: string;
  imported_at: Timestamp;
  imported_by: string;
  row_count: number;
  inserted_count: number;
  /** planned行と消込できた件数 */
  matched_count: number;
  duplicate_count: number;
  error_rows: number;
};

// ---------------------------------------------------------------------------
// 支払い先マスタ・登録依頼・通知(v3.5)
// ---------------------------------------------------------------------------

/** 支払い先マスタ。申請フォームは選択のみ(自由入力禁止・表記ゆれを構造的に防ぐ) */
export type Vendor = {
  id: string;
  /** 管理番号 V-xxxx */
  code: string;
  name: string;
  bank_info: string | null;
  note: string | null;
  active: boolean;
  created_by: string;
  created_at: Timestamp;
  updated_at: Timestamp;
};

/** 新しい支払い先の登録依頼(申請者→経理)。経理がマスタ管理で承認して vendors に登録する */
export type VendorRequest = {
  id: string;
  name: string;
  note: string | null;
  status: 'open' | 'done' | 'dismissed';
  requested_by: string;
  requested_at: Timestamp;
  resolved_by: string | null;
  resolved_at: Timestamp | null;
  /** 登録された vendor のID(done のとき) */
  vendor_id: string | null;
};

/** 進捗通知。申請一覧の赤●とメニューバッジの実体(localStorage禁止のためFirestoreに置く) */
export type AppNotification = {
  id: string;
  /** 宛先ユーザー */
  user_id: string;
  transaction_id: string;
  /** 例)「K-2026-0012 が承認されました(支払い待ち)」 */
  message: string;
  /**
   * action=操作依頼(承認/支払い/受領/完了確認の各画面での作業。バッジも該当画面に付く)
   * info=進捗連絡(自分の申請の状況。バッジは申請一覧に付く)。未設定の旧データはinfo扱い
   */
  kind?: 'action' | 'info';
  /**
   * 操作依頼の宛先画面(2026-08-26 ページ分割)。
   * approve=承認 / pay=支払い / receive=受領 / confirm=完了確認 /
   * acct_confirm=経理確認(支払い済み申請・2026-08-27)。
   * info通知は null(申請一覧)。未設定の旧action通知は approve 扱い
   */
  stage?: 'approve' | 'pay' | 'receive' | 'confirm' | 'acct_confirm' | null;
  read: boolean;
  created_at: Timestamp;
};

export type NotificationKind = NonNullable<AppNotification['kind']>;
export type NotificationStage = NonNullable<AppNotification['stage']>;

// ---------------------------------------------------------------------------
// 固定費マスタ(フェーズ2.5)
// ---------------------------------------------------------------------------

/** 計上日ルール: 末日 or 指定日 */
/** 日にち: 1〜31 または 'end'(末日) */
export type DayOrEnd = number | 'end';

/**
 * 計上日ルール。2026-09-08 改修後は「支払日の前々月〜翌々月」＋日にち(relative)。
 * month_end / day_of_month は旧データ(対象月基準)の互換用
 */
export type PostingRule =
  | { kind: 'relative'; month_offset: -2 | -1 | 0 | 1 | 2; day: DayOrEnd }
  | { kind: 'month_end' }
  | { kind: 'day_of_month'; day: number };

/**
 * 支払日ルール(2026-09-08 改修): 毎月の支払日(1〜31 または 'end'=末日)。支払日が基準で、計上日はこれに対する相対で決める。
 * same_month / next_month / next_next_month / month_end は旧データ(対象月基準)の互換用
 */
export type PaymentRule =
  | { kind: 'day_of_month'; day: DayOrEnd }
  | { kind: 'same_month' | 'next_month' | 'next_next_month'; day: DayOrEnd }
  | { kind: 'month_end' };

export type AmountRevision = {
  amount: Yen;
  /** この金額が適用される最初の月('YYYY-MM') */
  effective_from: MonthStr;
  changed_by: string;
  changed_at: Timestamp;
};

/** 繰り返しの頻度(2026-09-07): 毎月 / 毎週(weekday は 0=日〜6=土) */
export type RecurringFrequency = { kind: 'monthly' } | { kind: 'weekly'; weekday: number };

/** 支払日が土日祝のときの扱い(2026-09-07): 前の平日 / 後の平日 / そのまま */
export type HolidayShift = 'before' | 'after' | 'none';

/**
 * 繰り返し支払い(旧: 固定費マスタ)。家賃・水道光熱費・リースなど、決まった周期で支払うもの。
 * 各回は「支払い待ち」として自動で作られ、経理が「支払い方法を入力する」→ 承認者が「完了確認」でロックする(2026-09-07 指示)。
 */
export type RecurringCost = {
  id: string;
  name: string;
  company_id: string;
  department_id: DepartmentId;
  /** 拠点などの階層ノード(任意) */
  hierarchy_node_id: string | null;
  account_id: string;
  sub_account_id: string;
  amount: Yen;
  payment_method: PaymentMethod;
  /** 支払元の口座(CF支払予定の生成先) */
  cash_account_id: string | null;
  posting_rule: PostingRule;
  payment_rule: PaymentRule;
  /** 頻度(2026-09-07)。省略時は毎月 */
  frequency?: RecurringFrequency;
  /** 支払日が土日祝のときの扱い(2026-09-07)。省略時は後の平日 */
  holiday_shift?: HolidayShift;
  /** 毎回金額が違う(amount は目安)。省略時 false=完全固定 */
  amount_varies?: boolean;
  /** 適用期間('YYYY-MM')。end_month が null なら無期限 */
  start_month: MonthStr;
  end_month: MonthStr | null;
  counterparty: string | null;
  description: string | null;
  active: boolean;
  /** 金額改定の履歴(新しいものが後ろ) */
  amount_history: AmountRevision[];
  created_by: string;
  created_at: Timestamp;
  updated_by: string;
  updated_at: Timestamp;
};

/** 自動計上の実行履歴。同じ月の二重生成を防ぐ */
export type RecurringRun = {
  id: string;
  month: MonthStr;
  executed_at: Timestamp;
  executed_by: string;
  created_count: number;
  skipped_count: number;
};

// ---------------------------------------------------------------------------
// 予算(フェーズ2)
// ---------------------------------------------------------------------------

export type Budget = {
  id: string;
  fiscal_year: number;
  month: MonthStr;
  department_id: DepartmentId;
  account_id: string;
  amount: Yen;
  updated_by: string;
  updated_at: Timestamp;
};

// ---------------------------------------------------------------------------
// 設定・監査
// ---------------------------------------------------------------------------

export type FiscalSettings = { start_month: number };
/** 繰り返し支払い(2026-09-07): 自動処理のON/OFFと、支払日の何日前に経理へ知らせるか */
export type RecurringSettings = {
  auto_enabled: boolean;
  /** 支払日の何日前に経理へ知らせるか */
  notice_days_before: number;
  /** 支払いページ(③-3)に出す日数: 支払日の何日前から(2026-09-08 指示・既定7日) */
  display_days_before: number;
};
export type TaxSettings = { mode: 'inclusive' };
export type PlVisibilitySettings = { company_pl_roles: UserRole[] };

export type AuditLog = {
  id: string;
  target_type: 'transaction' | 'user' | 'master';
  target_id: string;
  action: string;
  by: string;
  at: Timestamp;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
};

// ---------------------------------------------------------------------------
// 共通サマリー5行の契約(変更禁止)
// ---------------------------------------------------------------------------

/**
 * 全事業部共通のサマリー。全社PLはこの5行の単純合算のみで構成される。
 * 事業部固有の項目を全社側に漏らさないこと。
 */
export type SummaryLine = {
  /** 売上 */
  revenue: Yen;
  /** 原価 */
  cogs: Yen;
  /** 粗利 = revenue - cogs */
  grossProfit: Yen;
  /** 経費 */
  expense: Yen;
  /** 部署利益 = grossProfit - expense */
  departmentProfit: Yen;
};

export type DepartmentSummary = {
  budget: SummaryLine;
  actual: SummaryLine;
};

/**
 * 各事業部モジュールが必ず実装するインターフェース。
 * **この型は変更しない。** 事業部内の計算がどれだけ複雑でもここは5行に畳む。
 */
export interface DepartmentModule {
  departmentId: DepartmentId;
  /** month は 'YYYY-MM'(JST)。実績は status==='confirmed' のみを含める */
  getMonthlySummary(month: MonthStr): Promise<DepartmentSummary>;
}

/** 売上・原価・経費から粗利と部署利益を導出する(計算式を1か所に固定) */
export function buildSummaryLine(input: { revenue: Yen; cogs: Yen; expense: Yen }): SummaryLine {
  const grossProfit = input.revenue - input.cogs;
  return {
    revenue: input.revenue,
    cogs: input.cogs,
    grossProfit,
    expense: input.expense,
    departmentProfit: grossProfit - input.expense,
  };
}

/** 全社PL用: 5行の単純合算 */
export function sumSummaryLines(lines: SummaryLine[]): SummaryLine {
  return lines.reduce<SummaryLine>(
    (acc, l) => ({
      revenue: acc.revenue + l.revenue,
      cogs: acc.cogs + l.cogs,
      grossProfit: acc.grossProfit + l.grossProfit,
      expense: acc.expense + l.expense,
      departmentProfit: acc.departmentProfit + l.departmentProfit,
    }),
    { revenue: 0, cogs: 0, grossProfit: 0, expense: 0, departmentProfit: 0 },
  );
}
